import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { LightningMapService } from 'src/app/main/side-bar/lightning/lightning-dashboard/lightning-map/lightning-map.service';

@Component({
  selector: 'app-lightning-playbar',
  templateUrl: './lightning-playbar.component.html',
  styleUrls: ['./lightning-playbar.component.scss']
})
export class LightningPlaybarComponent implements OnChanges, OnDestroy {
  @Input() labelStrings: any[] = [];
  @Input() miniChartData: any = null;
  @Input() miniChartDataField: string = 'categoryAreaPercentage';
  @Input() queryParams: any = {};
  @Output() groupSelected = new EventEmitter<string>();

  // ── Observed-mode: hourly block navigation ─────────────────────────────────
  activeHourlyIndex = 0;

  // ── Inner play bar (5-min intervals for observed / hourly-daily for forecast) ─
  // OLD: windowed forecast play bar constants (kept for forecast mode)
  readonly PAGE_SIZE = 12;
  windowStart = 0;
  activeIndex = 0;
  isPlaying = false;

  tooltip: { show: boolean; top: number; left: number; data: any; label?: string | null } = { show: false, top: 0, left: 0, data: null, label: null };
  private playTimer: any = null;

  readonly categories = ['No Lightning', 'Light', 'Moderate', 'Severe'];
  readonly colorMap: any = { 'No Lightning': '#FFFFFF', 'Light': '#f6f94b', 'Moderate': '#fdba12', 'Severe': '#f92509' };

  constructor(private lightningMapSrv: LightningMapService) { }

  // ── Mode detection ──────────────────────────────────────────────────────────
  get isObservedMode(): boolean { return this.queryParams && this.queryParams.lightningView === 'observed'; }

  // ── Observed: hourly block accessors (sorted descending: latest = index 0) ─
  get hourlyBlocks(): number[] { return this.lightningMapSrv.hourlyTimeBlocks || []; }
  get prevHourlyBlock(): number | null {
    const ts = this.hourlyBlocks[this.activeHourlyIndex + 1];
    return ts != null ? ts : null;
  }
  get activeHourlyBlock(): number | null {
    const ts = this.hourlyBlocks[this.activeHourlyIndex];
    return ts != null ? ts : null;
  }
  get nextHourlyBlock(): number | null {
    const ts = this.hourlyBlocks[this.activeHourlyIndex - 1];
    return ts != null ? ts : null;
  }
  get canNavLeft(): boolean { return this.activeHourlyIndex < this.hourlyBlocks.length - 1; }
  get canNavRight(): boolean { return this.activeHourlyIndex > 0; }

  getHourlyRange(ts: number): string {
    const d = new Date(ts);
    const startH = d.getHours();
    const endH = (startH + 1) % 24;
    const startHr = startH % 12 || 12;
    const endHr = endH % 12 || 12;
    return `${String(startHr).padStart(2, '0')}-${String(endHr).padStart(2, '0')}`;
  }

  getHourlyPeriod(ts: number): string {
    return new Date(ts).getHours() >= 12 ? 'PM' : 'AM';
  }

  selectHourlyBlock(index: number) {
    if (index < 0 || index >= this.hourlyBlocks.length) return;
    this.activeHourlyIndex = index;
    this.stopPlay();
    if (this.lightningMapSrv.gisMapComp) {
      this.lightningMapSrv.loadHeatMapLayersForHour(this.lightningMapSrv.gisMapComp, this.hourlyBlocks[index]);
      this.lightningMapSrv.observedHourChanged$.next(this.hourlyBlocks[index]);
    }
  }

  navLeftHourly() { if (this.canNavLeft) this.selectHourlyBlock(this.activeHourlyIndex + 1); }
  navRightHourly() { if (this.canNavRight) this.selectHourlyBlock(this.activeHourlyIndex - 1); }

  // ── Inner play bar (unchanged logic, used for forecast AND 5-min observed) ─

  get visibleBlocks(): any[] {
    return this.labelStrings.slice(this.windowStart, this.windowStart + this.PAGE_SIZE);
  }
  get canScrollLeft(): boolean { return this.windowStart > 0; }
  get canScrollRight(): boolean { return this.windowStart + this.PAGE_SIZE < this.labelStrings.length; }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.queryParams) {
      // Reset hourly index whenever params change (e.g. view switch or location drill-down)
      this.activeHourlyIndex = 0;
    }
    if (changes.labelStrings && this.labelStrings && this.labelStrings.length > 0) {
      this.windowStart = 0;
      this.activeIndex = 0;
      this.stopPlay();
      // Observed: start at latest 5-min slot; forecast: start at first slot
      const index = this.queryParams.lightningView === 'observed' ? this.labelStrings.length - 1 : 0;
      setTimeout(() => this.selectBlock(index), 0);
    }
  }

  selectBlock(index: number) {
    if (index < 0 || index >= this.labelStrings.length) return;
    this.activeIndex = index;
    if (this.labelStrings[index]) {
      this.groupSelected.emit(this.labelStrings[index].groupName);
    }
    this.ensureVisible(index);
  }

  private ensureVisible(index: number) {
    if (index < this.windowStart) {
      this.windowStart = index;
    } else if (index >= this.windowStart + this.PAGE_SIZE) {
      this.windowStart = Math.max(0, index - this.PAGE_SIZE + 1);
    }
  }

  scrollLeft() { this.windowStart = Math.max(0, this.windowStart - this.PAGE_SIZE); }
  scrollRight() {
    this.windowStart = Math.min(
      Math.max(0, this.labelStrings.length - this.PAGE_SIZE),
      this.windowStart + this.PAGE_SIZE
    );
  }

  togglePlay() { if (this.isPlaying) { this.stopPlay(); } else { this.startPlay(); } }

  private startPlay() {
    if (!this.labelStrings.length) return;
    this.isPlaying = true;
    this.playTimer = setInterval(() => {
      const next = (this.activeIndex + 1) % this.labelStrings.length;
      this.selectBlock(next);
    }, 1200);
  }

  private stopPlay() {
    this.isPlaying = false;
    if (this.playTimer) { clearInterval(this.playTimer); this.playTimer = null; }
  }

  // ── Mini chart helpers (used by forecast play bar) ─────────────────────────

  private findNearestMiniChartKey(groupName: string): string | null {
    if (!this.miniChartData) return null;
    const slotKey = Object.keys((this.miniChartData[this.categories[0]] || {}))[0];
    if (!slotKey) return null;
    const slot = this.miniChartData[this.categories[0]] && this.miniChartData[this.categories[0]][slotKey];
    if (!slot) return null;
    const tsKeys = Object.keys(slot).filter(k => /^\d{13}$/.test(k));
    if (!tsKeys.length) return null;
    const target = +groupName;
    return tsKeys.reduce((best, ts) => Math.abs(+ts - target) < Math.abs(+best - target) ? ts : best);
  }

  getMiniChartBars(groupName: string): { color: string; pct: number; cat: string; count: number }[] {
    if (!this.miniChartData) return [];
    const slotKey = Object.keys((this.miniChartData[this.categories[0]] || {}))[0];
    if (!slotKey) return [];

    const getCount = (cat: string, key: string): number => {
      const slot = this.miniChartData[cat] && this.miniChartData[cat][slotKey];
      return slot ? (+slot[key] || 0) : 0;
    };

    let counts = this.categories.map(cat => ({ cat, count: getCount(cat, groupName) }));

    if (counts.every(c => c.count === 0) && /^\d{13}$/.test(groupName)) {
      // fallback 1: yyyymmdd key (7-day forecast)
      const d = new Date(+groupName);
      const yyyymmdd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      counts = this.categories.map(cat => ({ cat, count: getCount(cat, yyyymmdd) }));
    }

    if (counts.every(c => c.count === 0)) {
      // fallback 2: nearest hour-block timestamp (observed 5-min blocks)
      const nearestKey = this.findNearestMiniChartKey(groupName);
      if (nearestKey) {
        counts = this.categories.map(cat => ({ cat, count: getCount(cat, nearestKey) }));
      }
    }

    const total = counts.reduce((s, c) => s + c.count, 0);
    if (total === 0) return [];
    return counts
      .filter(c => c.count > 0)
      .map(c => ({ color: this.colorMap[c.cat], pct: Math.round((c.count / total) * 100), cat: c.cat, count: c.count }));
  }

  getTooltipData(groupName: string): { color: string; pct: number; cat: string; count: number }[] {
    if (!this.miniChartData) return [];
    const slotKey = Object.keys((this.miniChartData[this.categories[0]] || {}))[0];
    if (!slotKey) return [];

    const getCount = (cat: string, key: string): number => {
      const slot = this.miniChartData[cat] && this.miniChartData[cat][slotKey];
      return slot ? (+slot[key] || 0) : 0;
    };

    let counts = this.categories.map(cat => ({ cat, count: getCount(cat, groupName) }));

    if (counts.every(c => c.count === 0) && /^\d{13}$/.test(groupName)) {
      // fallback 1: yyyymmdd key (7-day forecast)
      const d = new Date(+groupName);
      const yyyymmdd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      counts = this.categories.map(cat => ({ cat, count: getCount(cat, yyyymmdd) }));
    }

    if (counts.every(c => c.count === 0)) {
      // fallback 2: nearest hour-block timestamp (observed 5-min blocks)
      const nearestKey = this.findNearestMiniChartKey(groupName);
      if (nearestKey) {
        counts = this.categories.map(cat => ({ cat, count: getCount(cat, nearestKey) }));
      }
    }

    const total = counts.reduce((s, c) => s + c.count, 0);
    return counts.map(c => ({
      color: this.colorMap[c.cat],
      pct: total > 0 ? Math.round((c.count / total) * 100) : 0,
      cat: c.cat,
      count: c.count
    }));
  }

  showTooltip(event: MouseEvent, groupName: string, label?: string) {
    if (!this.miniChartData || (this.queryParams && this.queryParams.locType === 'BLOCK')) return;
    const bars = this.getTooltipData(groupName);
    if (!bars.length) return;
    const el = (event.currentTarget || event.target) as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.tooltip = { show: true, top: rect.top - 8, left: rect.left + rect.width / 2, data: bars, label: label || null };
  }

  hideTooltip() { this.tooltip.show = false; }

  ngOnDestroy() { this.stopPlay(); }
}
